import type {
  AssetApprovalChain,
  AssetApprovalChainStep,
  AssetApprovalChainStepAssignee,
} from '@/entities/asset-approval-chain';
import { i18n } from '@/shared/i18n';

/** One reviewer's row: the assignee, and their approval on this step if they gave one. */
export interface StepReviewer {
  userId: string;
  name: string;
  avatarUrl: string | null;
  approval: AssetApprovalChainStep['approvals'][number] | null;
  /**
   * Whether this row counts as signed off. Not derived from `approval` alone: a manager
   * clearing the step signs off in every assignee's place, and no approval record then
   * carries the reviewer's id. Kept separate from `approvedAt` so a row is never judged
   * unapproved just because a timestamp is missing.
   */
  isApproved: boolean;
  /** When the sign-off landed, for display. */
  approvedAt: AssetApprovalChainStep['approvals'][number]['createdAt'] | null;
  /**
   * Who entered the approval, when it was not the reviewer themselves. Either a manager who
   * cleared the whole step, or, on rows written before a sign-off was required to be the
   * caller's own, whoever recorded it.
   */
  approvedByName: string | null;
}

/** A manager's step-level override, if one stands on this step. */
export interface StepOverride {
  userId: string;
  name: string;
  createdAt: NonNullable<AssetApprovalChainStep['override']>['createdAt'];
}

/**
 * Null unless a manager cleared the whole step. An override is its own record, so it never
 * appears among `approvals` and never counts towards the threshold.
 */
export function stepOverride(step: AssetApprovalChainStep): StepOverride | null {
  const override = step.override;
  if (!override) return null;

  return {
    userId: override.userId,
    name: override.name ?? i18n.t('projects:approveAssetStep.approvalTimeline.unknown'),
    createdAt: override.createdAt,
  };
}

/**
 * Assignees first, in roster order, then anyone who approved without being assigned — a
 * reviewer taken off the step after signing off, or a row from when a manager could sign
 * off in somebody else's name.
 */
export function stepReviewers(step: AssetApprovalChainStep): StepReviewer[] {
  const unknown = () => i18n.t('projects:approveAssetStep.approvalTimeline.unknown');
  const approvalByUserId = new Map(step.approvals.map((approval) => [approval.userId, approval]));
  const override = stepOverride(step);

  const assigned = step.assignees.map((assignee: AssetApprovalChainStepAssignee) => {
    const approval = approvalByUserId.get(assignee.userId) ?? null;

    return {
      userId: assignee.userId,
      // The generated client widens this to string, but UserSummaryDto declares it nullable
      // and a user the platform lookup missed arrives as null.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- see above
      name: assignee.user.name ?? unknown(),
      avatarUrl: assignee.user.avatarUrl,
      approval,
      isApproved: approval !== null || override !== null,
      approvedAt: approval?.createdAt ?? override?.createdAt ?? null,
      approvedByName: approval ? (approval.approvedByName ?? null) : (override?.name ?? null),
    };
  });

  const assignedIds = new Set(step.assignees.map((assignee) => assignee.userId));
  const unassigned = step.approvals
    .filter((approval) => !assignedIds.has(approval.userId))
    .map((approval) => ({
      userId: approval.userId,
      name: approval.name ?? unknown(),
      avatarUrl: null,
      approval,
      isApproved: true,
      approvedAt: approval.createdAt,
      approvedByName: approval.approvedByName ?? null,
    }));

  return [...assigned, ...unassigned];
}

export function requiredApprovalCount(step: AssetApprovalChainStep): number {
  switch (step.approvalThresholdType) {
    case 'all':
      return step.assignees.length;
    case 'at_least_n':
      return step.approvalThresholdCount ?? step.assignees.length;
    case 'at_least_one':
    default:
      return 1;
  }
}

export function thresholdRuleLabel(step: AssetApprovalChainStep): string {
  switch (step.approvalThresholdType) {
    case 'all':
      return i18n.t('projects:approveAssetStep.timelineUtils.allMustApprove');
    case 'at_least_n':
      return i18n.t('projects:approveAssetStep.timelineUtils.atLeastNApprove', {
        count: requiredApprovalCount(step),
      });
    case 'at_least_one':
    default:
      return i18n.t('projects:approveAssetStep.timelineUtils.anyOneApproves');
  }
}

/**
 * Counted against the reviewer roster rather than the assignee list: an approval can come
 * from somebody the step never named, and would otherwise read as "2/1 approved".
 *
 * Both sides come from the rendered rows rather than `approvals`, which holds no row for an
 * override. Counting that array would print "0/3 approved" under three ticked reviewers on
 * a step a manager cleared.
 */
export function stepThresholdLabel(step: AssetApprovalChainStep): string {
  const reviewers = stepReviewers(step);
  // A role's holders are not listed, so with a role the step counts towards what it needs.
  const total = step.approverRole
    ? Math.max(reviewers.length, requiredApprovalCount(step))
    : reviewers.length;

  return i18n.t('projects:approveAssetStep.timelineUtils.ruleAndProgress', {
    rule: thresholdRuleLabel(step),
    approved: reviewers.filter((reviewer) => reviewer.isApproved).length,
    total,
  });
}

/**
 * The threshold rule and progress, plus what a locked step is waiting on — the only cue
 * a screen reader gets that the step is blocked, since the rail itself is decorative.
 */
export function stepStatusLabel(step: AssetApprovalChainStep): string {
  const threshold = stepThresholdLabel(step);
  if (step.state !== 'locked') return threshold;

  const waiting = step.waitingOnStepPosition
    ? i18n.t('projects:approveAssetStep.timelineUtils.waitingOnStep', {
        position: step.waitingOnStepPosition,
      })
    : i18n.t('projects:approveAssetStep.timelineUtils.waitingForEarlierSteps');

  return i18n.t('projects:approveAssetStep.timelineUtils.thresholdAndWaiting', {
    threshold,
    waiting,
  });
}

export function stepsApprovedLabel(chain: AssetApprovalChain): string {
  const steps = chain.workflowDefinition.steps;
  return i18n.t('projects:approveAssetStep.timelineUtils.stepsApproved', {
    approved: steps.filter((step) => step.state === 'met').length,
    count: steps.length,
  });
}

export function executionModeLabel(chain: AssetApprovalChain): string {
  return chain.workflowDefinition.executionMode === 'any_order'
    ? i18n.t('projects:approveAssetStep.timelineUtils.anyOrder')
    : i18n.t('projects:approveAssetStep.timelineUtils.inOrder');
}

export function orderedSteps(chain: AssetApprovalChain): AssetApprovalChainStep[] {
  return [...chain.workflowDefinition.steps].sort((a, b) => a.position - b.position);
}
